import type { SupabaseClient } from '@supabase/supabase-js';

type NavigationClient = Pick<SupabaseClient, 'from'>;
type NavigationStorage = Pick<Storage, 'getItem' | 'setItem'>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function browserSessionStorage(): NavigationStorage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

// Navigation hints only: the itinerary page/API must still authorize access.
export function rememberTravelerItinerary(
  itineraryId: string,
  userId: string | undefined,
  storage = browserSessionStorage(),
): void {
  if (!userId || !UUID_PATTERN.test(itineraryId)) return;
  try {
    storage?.setItem(`kova-traveler-itinerary:${userId}`, itineraryId);
  } catch {
    // Browsers can disable storage; membership lookup remains available.
  }
}

/** Resolve a signed-in user's landing page without treating every session as agency access. */
export async function getSignedInDestination(
  client: NavigationClient,
  userId: string,
  storage = browserSessionStorage(),
): Promise<string | null> {
  const { data: profile, error: profileError } = await client
    .from('profiles')
    .select('id')
    .eq('id', userId)
    .maybeSingle();

  if (profileError) throw profileError;
  // A legitimate agent can also be a traveler. Do not classify by user metadata.
  if (profile) return '/kovadash';

  try {
    const remembered = storage?.getItem(`kova-traveler-itinerary:${userId}`);
    if (remembered && UUID_PATTERN.test(remembered)) return `/itinerary/${remembered}`;
  } catch {
    // Continue with the authenticated membership lookup.
  }

  const { data: travelers, error: travelerError } = await client
    .from('itinerary_travelers')
    .select('itinerary_ids')
    .eq('user_id', userId);

  if (travelerError) throw travelerError;
  for (const traveler of travelers ?? []) {
    for (const itineraryId of traveler.itinerary_ids ?? []) {
      if (typeof itineraryId === 'string' && UUID_PATTERN.test(itineraryId)) {
        return `/itinerary/${itineraryId}`;
      }
    }
  }

  // No agency profile or itinerary: allow the login form instead of redirecting in a loop.
  return null;
}
